import { IFluidObject } from '@fluidframework/core-interfaces';
import { PresenceColorProvider } from '@fluidx/office-fluid-types';
import { resolvePresenceColor, strictType } from '@fluidx/utilities';
import { PostMessageSender, PostMessageHandlerFactory } from '../Shared/PostMessage';
import { PairingValue } from '../MessageTypes';
import {
  MessageTypes_PresenceColorProvider_GetColor,
  Message_PresenceColorProvider_GetColorsRequest,
  Message_PresenceColorProvider_GetColorsResponse
} from '../MessageTypes/PresenceColorProviderMessageTypes';

export function getHostSidePresenceColorProvider(
  scope: IFluidObject
): {
  scopePairingValue?: PairingValue<PresenceColorProvider>;
  scopeHandler?: PostMessageHandlerFactory;
} {
  if (!scope.PresenceColorProvider) {
    return {};
  }

  return {
    scopePairingValue: { found: true },
    scopeHandler: (sender: PostMessageSender) => {
      return {
        [MessageTypes_PresenceColorProvider_GetColor]: async (message) => {
          const userId = (message as Message_PresenceColorProvider_GetColorsRequest).payload;

          // Whether it's a string or a Promise<string>, await its resolution
          //   (which keeps the code shorter, and the async nature of postMessage
          //    has a far greater perf impact than the micro-task scheduling difference).
          const color = await resolvePresenceColor(scope.PresenceColorProvider!, userId, scope.NamespaceLogger);

          sender.sendReply(
            message,
            strictType<Message_PresenceColorProvider_GetColorsResponse>({
              payload: color
            })
          );
        }
      };
    }
  };
}

export function getGuestSidePresenceColorProvider(sender: PostMessageSender): PresenceColorProvider {
  return new IFramePresenceColorProviderProxy(sender);
}

/**
 * A proxy for the presence color provider
 * For efficiency's sake, having retrieved each user's color *once*,
 * it will cache the value to avoid having to do a postMessage roundtrip for the same user in the future.
 */
class IFramePresenceColorProviderProxy implements PresenceColorProvider {
  private cache: { [userId: string]: string } = {};

  constructor(private sender: PostMessageSender) {}

  public get PresenceColorProvider() {
    return this;
  }

  public getPresenceColorForUser(userId: string): string | Promise<string> {
    const possibleMatch = this.cache[userId];
    if (possibleMatch) {
      return possibleMatch;
    }

    return this.sender
      .sendWithReturn(
        strictType<Message_PresenceColorProvider_GetColorsRequest>({
          type: MessageTypes_PresenceColorProvider_GetColor,
          payload: userId
        })
      )
      .then((response) => {
        const color = (response as Message_PresenceColorProvider_GetColorsResponse).payload;
        this.cache[userId] = color;
        return color;
      });
  }
}
